'use client';

import { useLoadingStore } from './loading.store';
import { useCallback, useMemo } from 'react';

// Overall loading state
export const useIsLoading = () => {
  return useLoadingStore((state) => state.operations.size > 0);
};

// Get all loading operations
export const useLoadingOperations = () => {
  return useLoadingStore((state) =>
    Array.from(state.operations.entries()).map(([id, data]) => ({
      id,
      ...data,
    }))
  );
};

// Check if specific operation is loading
export const useIsOperationLoading = (operationId: string) => {
  return useLoadingStore((state) => state.operations.has(operationId));
};

// Get specific operation data
export const useOperationData = (operationId: string) => {
  return useLoadingStore((state) => state.operations.get(operationId));
};

// Get loading count
export const useLoadingCount = () => {
  return useLoadingStore((state) => state.operations.size);
};

// Get operations with progress
export const useOperationsWithProgress = () => {
  return useLoadingStore((state) =>
    Array.from(state.operations.entries())
      .filter(([, data]) => data.progress !== undefined && data.progress > 0)
      .map(([id, data]) => ({ id, ...data }))
  );
};

// Hook for managing loading state of a specific operation
export const useOperationLoading = (operationId: string) => {
  const startLoading = useLoadingStore((state) => state.startLoading);
  const stopLoading = useLoadingStore((state) => state.stopLoading);
  const updateProgress = useLoadingStore((state) => state.updateProgress);
  const updateMessage = useLoadingStore((state) => state.updateMessage);
  const isLoading = useIsOperationLoading(operationId);
  const operationData = useOperationData(operationId);

  const start = useCallback(
    (message?: string) => {
      startLoading(operationId, message);
    },
    [startLoading, operationId]
  );

  const stop = useCallback(() => {
    stopLoading(operationId);
  }, [stopLoading, operationId]);

  const setProgress = useCallback(
    (progress: number) => {
      updateProgress(operationId, progress);
    },
    [updateProgress, operationId]
  );

  const setMessage = useCallback(
    (message: string) => {
      updateMessage(operationId, message);
    },
    [updateMessage, operationId]
  );

  return useMemo(
    () => ({
      start,
      stop,
      setProgress,
      setMessage,
      isLoading,
      progress: operationData?.progress,
      message: operationData?.message,
      duration: operationData ? Date.now() - operationData.startTime : null,
    }),
    [start, stop, setProgress, setMessage, isLoading, operationData]
  );
};

// Actions
export const useLoadingActions = () => {
  const startLoading = useLoadingStore((state) => state.startLoading);
  const stopLoading = useLoadingStore((state) => state.stopLoading);
  const updateProgress = useLoadingStore((state) => state.updateProgress);
  const updateMessage = useLoadingStore((state) => state.updateMessage);
  const clearAll = useLoadingStore((state) => state.clearAll);
  const getOperationDuration = useLoadingStore(
    (state) => state.getOperationDuration
  );

  return {
    startLoading,
    stopLoading,
    updateProgress,
    updateMessage,
    clearAll,
    getOperationDuration,
  };
};
